import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { furnitureApi, movementsApi, furnitureTypesApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { usePreferences } from '../contexts/PreferencesContext';
import {
  ArrowLeft,
  Edit,
  Trash2,
  Package,
  MapPin,
  Calendar,
  Tag,
  Building2,
  User,
  Clock,
  ArrowLeftRight,
  Image
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const STATUS_CONFIG = {
  neuf: { label: 'Neuf', class: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  bon: { label: 'Bon', class: 'bg-blue-50 text-blue-700 border-blue-200' },
  usage: { label: 'Usagé', class: 'bg-amber-50 text-amber-700 border-amber-200' },
  hors_service: { label: 'Hors service', class: 'bg-rose-50 text-rose-700 border-rose-200' },
  en_maintenance: { label: 'En maintenance', class: 'bg-orange-50 text-orange-700 border-orange-200' }
};

const MOVEMENT_LABELS = {
  entree: 'Entrée en stock',
  sortie: 'Sortie de stock',
  transfert: 'Transfert',
  maintenance: 'Mise en maintenance',
  mise_au_rebut: 'Mise au rebut'
};

const FurnitureDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { formatCurrency } = usePreferences();
  const [furniture, setFurniture] = useState(null);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [typeLabels, setTypeLabels] = useState({});
  const { isGestionnaire, isAdmin } = useAuth();

  useEffect(() => {
    loadFurnitureTypes();
    loadData();
  }, [id]);

  // Load furniture types dynamically
  const loadFurnitureTypes = async () => {
    try {
      const response = await furnitureTypesApi.getAll();
      const labels = response.data.reduce((acc, ft) => {
        acc[ft.code] = ft.label;
        return acc;
      }, {});
      setTypeLabels(labels);
    } catch (error) {
      console.error('Erreur chargement types mobilier:', error);
    }
  };

  const loadData = async () => {
    try {
      const [furnitureRes, movementsRes] = await Promise.all([
        furnitureApi.getById(id),
        movementsApi.getByFurniture(id)
      ]);
      setFurniture(furnitureRes.data);
      setMovements(movementsRes.data);
    } catch (error) {
      console.error('Erreur chargement:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await furnitureApi.delete(id);
      navigate('/furniture');
    } catch (error) {
      console.error('Erreur suppression:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!furniture) {
    return (
      <div className="text-center py-16">
        <Package className="w-12 h-12 text-slate-300 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-slate-700">Mobilier non trouvé</h3>
        <Button onClick={() => navigate('/furniture')} className="mt-4">
          Retour à la liste
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="furniture-detail-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/furniture')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="font-heading text-2xl font-bold text-slate-900 tracking-tight">
                {typeLabels[furniture.type_mobilier] || furniture.type_mobilier}
              </h1>
              <Badge className={STATUS_CONFIG[furniture.etat]?.class || ''}>
                {STATUS_CONFIG[furniture.etat]?.label || furniture.etat}
              </Badge>
            </div>
            <p className="text-slate-500 font-mono mt-1">{furniture.code_barres}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {isGestionnaire() && (
            <Button
              variant="outline"
              onClick={() => navigate(`/furniture/${id}/edit`)}
              data-testid="edit-furniture-btn"
            >
              <Edit className="w-4 h-4 mr-2" />
              Modifier
            </Button>
          )}
          {isAdmin() && (
            <Button
              variant="destructive"
              onClick={() => setDeleteDialog(true)}
              data-testid="delete-furniture-btn"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Supprimer
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Catalog Image - if available */}
        {furniture.catalog_image_url && (
          <Card className="lg:col-span-3 border-blue-200 bg-gradient-to-r from-blue-50 to-white">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-6 items-center">
                <div className="relative w-full md:w-64 h-64 rounded-lg overflow-hidden bg-white shadow-sm border">
                  <img 
                    src={furniture.catalog_image_url} 
                    alt={`${furniture.marque} ${furniture.modele}`}
                    className="w-full h-full object-contain"
                  />
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="flex items-center gap-2 justify-center md:justify-start mb-2">
                    <Image className="w-5 h-5 text-blue-600" />
                    <span className="text-sm font-medium text-blue-600">Photo du catalogue</span>
                  </div>
                  <h3 className="text-2xl font-semibold text-slate-800">
                    {furniture.marque || 'Marque inconnue'}
                  </h3>
                  <p className="text-lg text-slate-600">{furniture.modele || 'Modèle inconnu'}</p>
                  {furniture.cout_achat && (
                    <p className="text-lg font-medium text-emerald-600 mt-2">
                      Valeur : {formatCurrency(furniture.cout_achat)}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Info */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="font-heading text-lg">Informations générales</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <div>
                <p className="text-sm text-slate-500 mb-1">Code-barres</p>
                <p className="font-mono text-lg font-medium">{furniture.code_barres}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Type</p>
                <p className="font-medium">{typeLabels[furniture.type_mobilier] || furniture.type_mobilier}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Marque</p>
                <p className="font-medium">{furniture.marque || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Modèle</p>
                <p className="font-medium">{furniture.modele || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Fournisseur</p>
                <p className="font-medium">{furniture.fournisseur || '-'}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">Référence</p>
                <p className="font-mono">{furniture.reference || '-'}</p>
              </div>
            </div>

            <div className="border-t pt-6">
              <h4 className="font-medium text-slate-700 mb-4">Acquisition</h4>
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-slate-500 mb-1">Date d'acquisition</p>
                  <p className="font-medium">
                    {furniture.date_acquisition
                      ? new Date(furniture.date_acquisition).toLocaleDateString('fr-FR')
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-slate-500 mb-1">Coût d'achat</p>
                  <p className="font-medium">
                    {furniture.cout_achat
                      ? formatCurrency(furniture.cout_achat)
                      : '-'}
                  </p>
                </div>
              </div>
            </div>

            {furniture.notes && (
              <div className="border-t pt-6">
                <h4 className="font-medium text-slate-700 mb-2">Notes</h4>
                <p className="text-slate-600">{furniture.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Location */}
        <Card>
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <MapPin className="w-5 h-5 text-slate-400" />
              Localisation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-slate-500 mb-1">Site</p>
              <p className="font-medium">{furniture.site_nom || 'Non affecté'}</p>
            </div>
            {furniture.batiment && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Bâtiment</p>
                <p className="font-medium">{furniture.batiment}</p>
              </div>
            )}
            {furniture.etage && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Étage</p>
                <p className="font-medium">{furniture.etage}</p>
              </div>
            )}
            {furniture.bureau && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Bureau</p>
                <p className="font-medium">{furniture.bureau}</p>
              </div>
            )}
            {furniture.service && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Service</p>
                <p className="font-medium">{furniture.service}</p>
              </div>
            )}
            {furniture.collaborateur && (
              <div>
                <p className="text-sm text-slate-500 mb-1">Collaborateur</p>
                <p className="font-medium">{furniture.collaborateur}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Movement History */}
        <Card className="lg:col-span-3">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <Clock className="w-5 h-5 text-slate-400" />
              Historique des mouvements
            </CardTitle>
            {isGestionnaire() && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(`/movements/new?furniture=${id}`)}
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                Nouveau mouvement
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {movements.length === 0 ? (
              <p className="text-center text-slate-400 py-8">Aucun mouvement enregistré</p>
            ) : (
              <div className="space-y-4">
                {movements.map((movement, index) => (
                  <div
                    key={movement.id}
                    className="flex items-start gap-4 p-4 rounded-lg bg-slate-50"
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      movement.type_mouvement === 'entree' ? 'bg-emerald-100 text-emerald-600' :
                      movement.type_mouvement === 'sortie' ? 'bg-rose-100 text-rose-600' :
                      movement.type_mouvement === 'maintenance' ? 'bg-orange-100 text-orange-600' :
                      movement.type_mouvement === 'mise_au_rebut' ? 'bg-rose-100 text-rose-600' :
                      'bg-blue-100 text-blue-600'
                    }`}>
                      <ArrowLeftRight className="w-5 h-5" />
                    </div>
                    <div className="flex-1">
                      <p className="font-medium text-slate-700">
                        {MOVEMENT_LABELS[movement.type_mouvement] || movement.type_mouvement}
                      </p>
                      {movement.commentaire && (
                        <p className="text-sm text-slate-500 mt-1">{movement.commentaire}</p>
                      )}
                      <p className="text-xs text-slate-400 mt-2 font-mono">
                        {new Date(movement.created_at).toLocaleString('fr-FR')}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog} onOpenChange={setDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmer la suppression</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer ce mobilier ?
              <br />
              <span className="font-mono text-slate-700">{furniture.code_barres}</span>
              <br />
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialog(false)}>
              Annuler
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FurnitureDetailPage;
